/**
 * WitnessDashPanel — arcade run for agent boundary crossings.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAnimationFrame } from "@csk/demo-ui";
import {
  ArcadeArenaCard,
  ArcadeArenaStage,
  ArcadeBlockedSlot,
  ArcadeControlButton,
  ArcadeKeyframes,
  ArcadeLogPanel,
  ArcadeMissionPrompt,
  ArcadePlayfieldFrame,
  ArcadePlayfieldGuideLine,
  ArcadePlayfieldGuideZone,
  ArcadePlayfieldMarker,
  ArcadeStatusDots,
  FlashOverlay,
  arcadeLaneCenter,
  arcadeOffsetLaneCenter,
  createArcadeHudStripProps,
  useArcadeActionHotkeys,
  useArcadeScore,
  useReducedMotion,
} from "../../../games/index.js";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import {
  WITNESS_DASH_LEVELS,
  createWitnessDashState,
  getWitnessDashLevel,
  legalWitnessDashCrossMoves,
  stepWitnessDash,
  tickWitnessDash,
  unwitnessedCrossingGenerated,
  witnessGateInActionZone,
  type WitnessDashGate,
  type WitnessDashState,
} from "../algorithms/witnessDash.js";
const AGENT_X = 18;
const PICKUP_LANE_OFFSETS = [-11, -3] as const;
const GATE_LANE_OFFSET = 7;
const LANE_COLORS = ["#22c55e", "#38bdf8", "#fbbf24"];
export function WitnessDashPanel(): React.JSX.Element {
  const [state, setState] = useState<WitnessDashState>(() =>
    createWitnessDashState(),
  );
  const [running, setRunning] = useState(true);
  const reducedMotion = useReducedMotion();
  const score = useArcadeScore({
    hitColor: "rgba(34,197,94,0.22)",
    missColor: "rgba(248,113,113,0.2)",
  });
  const previousStateRef = useRef(state);
  const level = useMemo(
    () => getWitnessDashLevel(state.levelId),
    [state.levelId],
  );
  const runDurationMs = useMemo(() => estimateRunDurationMs(level), [level]);
  const remainingSeconds = Math.max(0, (runDurationMs - state.timeMs) / 1000);
  const legalCrossings = useMemo(
    () => legalWitnessDashCrossMoves(state),
    [state],
  );
  const legalGateIds = useMemo(
    () => new Set(legalCrossings.map((move) => move.gateId)),
    [legalCrossings],
  );
  const frontGate = useMemo(() => firstVisibleGate(state.gates), [state.gates]);
  const witnessDots = (frontGate?.requiredWitnesses ?? []).map((witness) => ({
    label: witness,
    active: state.inventory.includes(witness),
  }));
  useAnimationFrame(
    ({ deltaMs }) => {
      setState((current) => tickWitnessDash(current, Math.min(deltaMs, 80)));
    },
    { enabled: running && state.status === "playing", maxFps: 60 },
  );
  const reset = (levelId = state.levelId): void => {
    const next = createWitnessDashState(levelId);
    previousStateRef.current = next;
    setState(next);
    setRunning(true);
    score.reset();
  };
  const moveLane = (lane: number): void => {
    setState((current) => stepWitnessDash(current, { type: "lane", lane }));
  };
  const crossGate = (gateId: string): void => {
    setState((current) => {
      const move = legalWitnessDashCrossMoves(current).find(
        (candidate) => candidate.gateId === gateId,
      );
      return move ? stepWitnessDash(current, move) : current;
    });
  };
  useEffect(() => {
    const previous = previousStateRef.current;
    previousStateRef.current = state;
    if (state.crossings > previous.crossings) {
      score.hit();
    }
    if (state.status === "escaped" && previous.status !== "escaped") {
      score.miss(panelTokens.accentRed);
    }
  }, [score, state]);
  useArcadeActionHotkeys({
    extraBindings: [
      {
        key: "ArrowUp",
        enabled: state.status === "playing" && state.lane > 0,
        onTrigger: (): void => {
          moveLane(state.lane - 1);
        },
      },
      {
        key: "ArrowDown",
        enabled:
          state.status === "playing" && state.lane < level.lanes.length - 1,
        onTrigger: (): void => {
          moveLane(state.lane + 1);
        },
      },
    ],
    includeEnter: false,
    primaryEnabled: state.status === "playing",
    onPrimary: (): void => {
      const move = legalCrossings.at(0);
      if (move) crossGate(move.gateId);
      else score.flash(panelTokens.accentRed);
    },
  });
  return (
    <PanelSurface.PanelShell
      title="Witness Dash"
      subtitle="Collect bridge witnesses · cross only with the complete tuple"
      accentColor={panelTokens.accentYellow}
    >
      <ArcadeKeyframes />
      <ArcadeArenaCard>
        <ArcadeArenaStage
          hudProps={createArcadeHudStripProps({
            stats: [
              {
                label: "crossings",
                value: `${String(state.crossings)}/${String(level.targetCrossings)}`,
                color: panelTokens.accentGreen,
              },
              {
                label: "integrity",
                value: state.integrity,
                color: panelTokens.accentBlue,
              },
              {
                label: "escaped",
                value: state.missed,
                color: panelTokens.accentRed,
              },
              {
                label: "unwitnessed",
                value: unwitnessedCrossingGenerated() ? "on" : "off",
                color: panelTokens.accentRed,
                title: "Unwitnessed boundary crossings are not generated.",
              },
            ],
            score: score.score,
            rank: score.rank,
            rankColor: panelTokens.accentYellow,
            scoreColor: panelTokens.text,
            borderColor: "rgba(255,255,255,0.1)",
            clock: {
              fraction: Math.max(0, remainingSeconds / (runDurationMs / 1000)),
              remaining: remainingSeconds,
              running: running && state.status === "playing",
            },
            timerAccent: panelTokens.accentYellow,
            timerDanger: panelTokens.accentRed,
            timerTrack: "rgba(148,163,184,0.16)",
            combo: score.combo,
            comboColor: panelTokens.accentYellow,
            comboMuted: panelTokens.muted,
            reducedMotion,
          })}
          levelSelectProps={{
            levels: WITNESS_DASH_LEVELS,
            activeLevelId: state.levelId,
            onSelect: reset,
            onReset: () => {
              reset();
            },
            accentColor: panelTokens.accentYellow,
            borderColor: panelTokens.border,
            mutedColor: panelTokens.muted,
            brightColor: panelTokens.text,
            activeBackground: "rgba(251,191,36,0.18)",
          }}
        >
          <ArcadePlayfieldFrame
            borderColor={panelTokens.border}
            background="radial-gradient(circle at 18% 20%, rgba(251,191,36,0.16), transparent 28%), radial-gradient(circle at 78% 28%, rgba(34,197,94,0.14), transparent 24%), linear-gradient(180deg, rgba(5,9,20,0.86), rgba(5,9,20,0.98))"
            accentColor={panelTokens.accentYellow}
            secondaryColor={panelTokens.accentGreen}
            minHeight={470}
            header={
              <ArcadeMissionPrompt
                label="Primary verb"
                title={
                  legalCrossings.length > 0
                    ? "Cross the lit boundary gate"
                    : "Collect the next witness tuple"
                }
                detail="Use Up/Down to choose a lane, then Space at a lit gate. Missing witness tuples do not create crossings."
                color={panelTokens.accentYellow}
                hotkey="Space"
                status={state.status}
              />
            }
          >
            <div className="relative h-[360px] min-h-[320px] overflow-hidden rounded-xl border border-white/10 bg-[#050914]">
              <ArcadePlayfieldGuideLine
                x={AGENT_X}
                className="border-l border-dashed border-emerald-200/70"
              />
              <ArcadePlayfieldGuideZone
                x={10}
                width={15}
                className="bg-yellow-300/5"
              />
              {level.lanes.map((lane, index) => (
                <LaneRail
                  key={lane}
                  lane={lane}
                  index={index}
                  count={level.lanes.length}
                  active={index === state.lane}
                  onSelect={moveLane}
                />
              ))}
              <ArcadePlayfieldMarker
                ariaHidden
                x={AGENT_X}
                y={arcadeLaneCenter(state.lane, level.lanes.length)}
                className="h-[36px] w-[28px] rounded-md border-2 border-solid border-emerald-100 bg-emerald-200/20 shadow-[0_0_20px_rgba(110,231,183,0.35)]"
              />
              {state.pickups.map((pickup, index) => (
                <ArcadePlayfieldMarker
                  key={pickup.id}
                  x={pickup.x}
                  y={arcadeOffsetLaneCenter(
                    pickup.lane,
                    level.lanes.length,
                    PICKUP_LANE_OFFSETS[index % PICKUP_LANE_OFFSETS.length],
                  )}
                  className="min-w-[52px] rounded-full border border-solid px-1 py-1.5 text-center font-mono text-[8.5px] font-bold sm:min-w-[72px] sm:px-2 sm:py-2 sm:text-[10px]"
                  borderColor={
                    LANE_COLORS[pickup.lane] ?? panelTokens.accentYellow
                  }
                  background="rgba(34,197,94,0.14)"
                  color="#bbf7d0"
                >
                  {pickup.witnessId}
                </ArcadePlayfieldMarker>
              ))}
              {state.gates.map((gate) => (
                <GateButton
                  key={gate.id}
                  gate={gate}
                  laneCount={level.lanes.length}
                  legal={legalGateIds.has(gate.id)}
                  onCross={crossGate}
                />
              ))}
            </div>
            <FlashOverlay
              token={score.flashToken}
              color={score.flashColor}
              reducedMotion={reducedMotion}
            />
          </ArcadePlayfieldFrame>
          <ArcadeLogPanel
            title="Witness tuple"
            event={state.lastEvent}
            borderColor={panelTokens.border}
            background="rgba(6,7,15,0.45)"
            textColor={panelTokens.text}
            badges={[
              PanelSurface.badge(
                running ? "running" : "paused",
                panelTokens.accentYellow,
              ),
              PanelSurface.badge(
                statusLabel(state.status),
                statusColor(state.status),
              ),
            ]}
          >
            <div className="mb-3 flex flex-wrap gap-2">
              {level.lanes.map((lane, index) => (
                <ArcadeControlButton
                  key={lane}
                  label={`Move to ${lane}`}
                  accent={
                    index === state.lane
                      ? panelTokens.accentYellow
                      : panelTokens.border
                  }
                  onClick={() => {
                    moveLane(index);
                  }}
                >
                  {lane}
                </ArcadeControlButton>
              ))}
              <ArcadeControlButton
                label={running ? "Pause run" : "Resume run"}
                accent={running ? panelTokens.border : panelTokens.accentYellow}
                onClick={() => {
                  setRunning((value) => !value);
                }}
              >
                {running ? "Pause" : "Run"}
              </ArcadeControlButton>
            </div>
            <div className="mb-3 flex flex-wrap gap-2">
              {state.inventory.length === 0
                ? PanelSurface.badge("tuple empty", panelTokens.muted)
                : state.inventory.map((witness, index) => (
                    <span key={`${witness}:${index}`}>
                      {PanelSurface.badge(witness, panelTokens.witnessHeld)}
                    </span>
                  ))}
            </div>
            {frontGate ? (
              <ArcadeStatusDots
                items={witnessDots}
                activeColor={panelTokens.accentGreen}
                inactiveColor={panelTokens.accentRed}
                activeText="held"
                inactiveText="need"
                className="mb-3"
              />
            ) : null}
            <PanelSurface.VerdictBanner
              tone={state.status === "escaped" ? "unsafe" : "safe"}
              title={
                state.status === "cleared"
                  ? "Run complete with witnessed crossings"
                  : state.status === "escaped"
                    ? "Witness discipline was not maintained"
                    : "Crossing moves appear only for complete tuples"
              }
              body={state.brief}
            />
            <ArcadeBlockedSlot
              label="Missing move"
              title="Unwitnessed crossing"
              detail="A gate only lights inside the action zone and only when every required witness is held."
              color={panelTokens.accentRed}
              className="mt-4"
            />
          </ArcadeLogPanel>
        </ArcadeArenaStage>
      </ArcadeArenaCard>
    </PanelSurface.PanelShell>
  );
}
function LaneRail({
  lane,
  index,
  count,
  active,
  onSelect,
}: {
  readonly lane: string;
  readonly index: number;
  readonly count: number;
  readonly active: boolean;
  readonly onSelect: (lane: number) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={() => {
        onSelect(index);
      }}
      className="absolute left-0 right-0 h-px border-0 border-t border-solid bg-transparent text-left"
      style={{
        top: arcadeLaneCenter(index, count),
        borderColor: active ? "#fde68a" : "rgba(148,163,184,0.22)",
        color: active ? "#fef3c7" : panelTokens.muted,
      }}
    >
      <span className="ml-2 inline-block max-w-[62px] -translate-y-7 truncate rounded bg-black/50 px-1.5 py-0.5 font-mono text-[9px] uppercase sm:ml-3 sm:max-w-none sm:-translate-y-6 sm:px-2 sm:py-1 sm:text-[11px]">
        {lane}
      </span>
    </button>
  );
}
function GateButton({
  gate,
  laneCount,
  legal,
  onCross,
}: {
  readonly gate: WitnessDashGate;
  readonly laneCount: number;
  readonly legal: boolean;
  readonly onCross: (gateId: string) => void;
}): React.JSX.Element {
  const inActionZone = witnessGateInActionZone(gate);
  return (
    <ArcadePlayfieldMarker
      as="button"
      aria-label={gate.label}
      disabled={!legal}
      onClick={() => {
        onCross(gate.id);
      }}
      x={gate.x}
      y={arcadeOffsetLaneCenter(gate.lane, laneCount, GATE_LANE_OFFSET)}
      className="flex min-h-14 w-[92px] flex-col items-center justify-center rounded-lg border-2 border-solid px-2 font-mono text-[9px] font-bold sm:min-h-16 sm:w-[112px] sm:text-[10px]"
      borderColor={legal ? "#22c55e" : "#f97316"}
      background={
        legal
          ? "rgba(34,197,94,0.18)"
          : inActionZone
            ? "rgba(124,45,18,0.44)"
            : "rgba(15,23,42,0.64)"
      }
      color={legal ? "#bbf7d0" : inActionZone ? "#fed7aa" : "#cbd5e1"}
      boxShadow={legal ? "0 0 20px rgba(34,197,94,0.35)" : "none"}
    >
      <span>{gate.regime}</span>
      <span className="text-[9px] opacity-85">
        {gate.requiredWitnesses.join("+")}
      </span>
      <span className="text-[8px] opacity-75">
        {legal ? "cross" : inActionZone ? "needs tuple" : "incoming"}
      </span>
    </ArcadePlayfieldMarker>
  );
}
function statusColor(status: WitnessDashState["status"]): string {
  if (status === "cleared") return panelTokens.accentGreen;
  if (status === "escaped") return panelTokens.accentRed;
  return panelTokens.accentBlue;
}
function statusLabel(status: WitnessDashState["status"]): string {
  if (status === "cleared") return "cleared";
  if (status === "escaped") return "escaped";
  return "playing";
}
function firstVisibleGate(
  gates: readonly WitnessDashGate[],
): WitnessDashGate | null {
  return (
    [...gates]
      .sort((left, right) => left.x - right.x)
      .find((gate) => gate.x >= 0) ?? null
  );
}
function estimateRunDurationMs(level: {
  readonly pickups: readonly {
    readonly spawnAtMs: number;
  }[];
  readonly gates: readonly {
    readonly spawnAtMs: number;
  }[];
}): number {
  const lastPickup = Math.max(
    0,
    ...level.pickups.map((item) => item.spawnAtMs),
  );
  const lastGate = Math.max(0, ...level.gates.map((item) => item.spawnAtMs));
  return Math.max(lastPickup, lastGate) + 12000;
}
